import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from '../contexts/AuthContext';
import RandevuTakvim, { TakvimGorunum, PersonelDoluluk } from '../components/randevu/RandevuTakvim';
import RandevuFormModal from '../components/randevu/RandevuFormModal';
import MusaitlikAyarlari from '../components/randevu/MusaitlikAyarlari';
import { useRandevuTanimlar, randevuProjeleriGetir } from '../components/randevu/useRandevuTanimlar';
import {
    RandevuOzet, RandevuVarsayilan, RandevuTuru, RandevuKaynagi, RandevuGenelAyar, DURUMLAR, KAYNAK_TURLERI, GUN_KISA,
    durumMeta, sonucAdi, tarihStr, tarihOku, yerelIso, gunBasi, gunEkle, haftaBasi, saatAraligi, randevuAc,
    hataMesaji, RANDEVU_DEGISTI_OLAYI, randevuDegisti,
} from '../components/randevu/randevuOrtak';
import '../components/takip/takip.css';
import '../components/randevu/randevu.css';

type Sekme = 'takvim' | 'ekip' | 'liste' | 'rapor' | 'musaitlik' | 'ayarlar';

// v1.24.0 — Randevu alma linki: her kopyalamada yeni bağlantı oluşur (önceki bağlantılar geçerli kalır)
const DavetBaglantisi: React.FC<{ onMusaitlik: () => void }> = ({ onMusaitlik }) => {
    const [d, setD] = useState<{ link: string; sistemAcik: boolean; personelAcik: boolean; calismaGunuVar: boolean; toplamDavet: number; kullanilan: number } | null>(null);
    const [durum, setDurum] = useState<'' | 'kopyalaniyor' | 'kopyalandi'>('');
    useEffect(() => { axiosInstance.get('/api/Randevular/davet').then(r => setD(r.data)).catch(() => { }); }, []);
    if (!d) return null;
    const tam = (l: string) => l.startsWith('http') ? l : `${window.location.origin}${l}`;
    const kopyala = async () => {
        setDurum('kopyalaniyor');
        try {
            const r = await axiosInstance.post('/api/Randevular/davet', { siteUrl: window.location.origin });
            const link = tam(r.data.link);
            try { await navigator.clipboard.writeText(link); } catch { window.prompt('Bağlantıyı kopyalayın:', link); }
            setD(r.data);
            setDurum('kopyalandi');
            window.setTimeout(() => setDurum(''), 2500);
        } catch { setDurum(''); }
    };
    const sorun = !d.sistemAcik ? 'Online randevu sistem genelinde kapalı (Ayarlar sekmesi).'
        : !d.personelAcik ? 'Takviminiz online randevuya kapalı.'
        : !d.calismaGunuVar ? 'Çalışma günleriniz tanımlı değil — müşteri boş saat göremez.' : null;
    return (
        <div className="rnd-kart" style={{ padding: '10px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', background: sorun ? '#fffbeb' : '#f0fdfa', borderColor: sorun ? '#fde68a' : '#99f6e4' }}>
            <i className="fas fa-link" style={{ color: sorun ? '#b45309' : '#0d9488' }}></i>
            <b style={{ fontSize: 13, color: '#0f172a' }}>Randevu alma linkim</b>
            <code style={{ fontSize: 12, color: '#334155', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 8px', flex: 1, minWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{tam(d.link)}</code>
            <button className="tkp-btn turuncu" onClick={kopyala} disabled={durum === 'kopyalaniyor'} title="Yeni bir bağlantı oluşturup panoya kopyalar">
                <i className={`fas ${durum === 'kopyalandi' ? 'fa-check' : durum === 'kopyalaniyor' ? 'fa-spinner fa-spin' : 'fa-copy'}`}></i>{durum === 'kopyalandi' ? 'Kopyalandı' : 'Kopyala'}
            </button>
            <span className="tkp-ipucu" style={{ width: '100%', margin: 0 }}>
                {sorun ? <><i className="fas fa-exclamation-triangle" style={{ color: '#b45309', marginRight: 4 }}></i>{sorun} <a href="#" onClick={e => { e.preventDefault(); onMusaitlik(); }}>Müsaitliğimi düzenle</a></>
                    : <>Her kopyalamada müşteriye özel yeni bir bağlantı oluşur; önceki bağlantılar da çalışır. {d.toplamDavet} bağlantıyla {d.kullanilan} randevu alındı.</>}
            </span>
        </div>
    );
};

// Görünüme göre tarih aralığı
function aralik(gorunum: TakvimGorunum | 'ekipHafta', tarih: Date): [Date, Date] {
    if (gorunum === 'gun' || gorunum === 'ekip') { const b = gunBasi(tarih); return [b, gunEkle(b, 1)]; }
    if (gorunum === 'hafta' || gorunum === 'ekipHafta') { const b = haftaBasi(tarih); return [b, gunEkle(b, 7)]; }
    const ayBas = new Date(tarih.getFullYear(), tarih.getMonth(), 1);
    const b = haftaBasi(ayBas);
    return [b, gunEkle(b, 42)];
}

function baslikMetni(gorunum: TakvimGorunum | 'ekipHafta', tarih: Date) {
    if (gorunum === 'gun' || gorunum === 'ekip') return tarih.toLocaleDateString('tr-TR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (gorunum === 'ay') return tarih.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
    const b = haftaBasi(tarih), s = gunEkle(b, 6);
    return `${b.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' })} – ${s.toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

function kaydir(gorunum: TakvimGorunum | 'ekipHafta', tarih: Date, yon: number) {
    if (gorunum === 'gun' || gorunum === 'ekip') return gunEkle(tarih, yon);
    if (gorunum === 'hafta' || gorunum === 'ekipHafta') return gunEkle(tarih, 7 * yon);
    return new Date(tarih.getFullYear(), tarih.getMonth() + yon, 1);
}

const Randevular: React.FC = () => {
    const { user } = useAuth();
    const ben = user?.kullaniciID ?? 0;
    const location = useLocation();
    const { tanimlar, yenile: tanimYenile } = useRandevuTanimlar();
    const [sekme, setSekme] = useState<Sekme>(() => (new URLSearchParams(location.search).get('sekme') as Sekme) || 'takvim');
    const [form, setForm] = useState<RandevuVarsayilan | null>(null);

    useEffect(() => {
        const s = new URLSearchParams(location.search).get('sekme') as Sekme | null;
        if (s) setSekme(s);
    }, [location.search]);

    const sekmeler: { s: Sekme; ad: string; ikon: string; goster: boolean }[] = [
        { s: 'takvim', ad: 'Takvim', ikon: 'fa-calendar-alt', goster: true },
        { s: 'ekip', ad: 'Ekip Takvimi', ikon: 'fa-users', goster: !!tanimlar?.yoneticiMi },
        { s: 'liste', ad: 'Liste', ikon: 'fa-list', goster: true },
        { s: 'rapor', ad: 'Raporlar', ikon: 'fa-chart-bar', goster: true },
        { s: 'musaitlik', ad: 'Müsaitliğim', ikon: 'fa-user-clock', goster: true },
        { s: 'ayarlar', ad: 'Ayarlar', ikon: 'fa-cog', goster: !!tanimlar?.tanimYetkisi },
    ];

    return (
        <div className="rnd-sayfa">
            <div className="rnd-bas">
                <h1><i className="fas fa-calendar-check" style={{ color: '#e67d21' }}></i>Randevular</h1>
                <button className="tkp-btn turuncu" style={{ marginLeft: 'auto', padding: '8px 14px', fontSize: 13.5 }} onClick={() => setForm({})}>
                    <i className="fas fa-plus"></i>Yeni Randevu
                </button>
            </div>
            <DavetBaglantisi onMusaitlik={() => setSekme('musaitlik')} />
            <div className="rnd-sekmeler">
                {sekmeler.filter(x => x.goster).map(x => (
                    <button key={x.s} className={sekme === x.s ? 'aktif' : ''} onClick={() => setSekme(x.s)}><i className={`fas ${x.ikon}`}></i>{x.ad}</button>
                ))}
            </div>

            {sekme === 'takvim' && <TakvimSekmesi ben={ben} onYeni={setForm} />}
            {sekme === 'ekip' && <EkipSekmesi onYeni={setForm} />}
            {sekme === 'liste' && <ListeSekmesi />}
            {sekme === 'rapor' && <RaporSekmesi />}
            {sekme === 'musaitlik' && (
                <div className="rnd-kart" style={{ padding: 18, maxWidth: 820 }}>
                    <MusaitlikAyarlari kullaniciID={ben} onKaydedildi={tanimYenile} />
                </div>
            )}
            {sekme === 'ayarlar' && tanimlar?.tanimYetkisi && <AyarlarSekmesi onDegisti={tanimYenile} />}

            {form && <RandevuFormModal varsayilan={form} onKapat={() => setForm(null)} />}
        </div>
    );
};

// ── Takvim ─────────────────────────────────────────────────────────────

const TakvimSekmesi: React.FC<{ ben: number; onYeni: (v: RandevuVarsayilan) => void }> = ({ ben, onYeni }) => {
    const { tanimlar } = useRandevuTanimlar();
    const [gorunum, setGorunum] = useState<TakvimGorunum>(() => (window.innerWidth < 768 ? 'gun' : 'hafta'));
    const [tarih, setTarih] = useState(new Date());
    const [personelID, setPersonelID] = useState<number | 'ekip'>(ben);
    const [turID, setTurID] = useState<number | ''>('');
    const [pasifGoster, setPasifGoster] = useState(false);
    const [randevular, setRandevular] = useState<RandevuOzet[]>([]);
    const [doluluk, setDoluluk] = useState<PersonelDoluluk[]>([]);
    const [yukleniyor, setYukleniyor] = useState(false);

    const [bas, son] = useMemo(() => aralik(gorunum, tarih), [gorunum, tarih]);

    const yukle = useCallback(async () => {
        setYukleniyor(true);
        try {
            const params: any = { baslangic: yerelIso(bas), bitis: yerelIso(son), turID: turID || undefined };
            if (personelID === 'ekip') params.kapsam = 'ekip'; else params.personelIDler = String(personelID);
            const [r, d] = await Promise.all([
                axiosInstance.get('/api/Randevular', { params }),
                personelID === 'ekip' ? Promise.resolve({ data: [] }) : axiosInstance.get('/api/Randevular/doluluk', { params: { baslangic: yerelIso(bas), bitis: yerelIso(son), personelIDler: String(personelID) } }),
            ]);
            setRandevular(r.data);
            setDoluluk(d.data);
        } catch { setRandevular([]); }
        finally { setYukleniyor(false); }
    }, [bas, son, personelID, turID]);
    useEffect(() => { yukle(); }, [yukle]);
    useEffect(() => {
        const h = () => yukle();
        window.addEventListener(RANDEVU_DEGISTI_OLAYI, h);
        return () => window.removeEventListener(RANDEVU_DEGISTI_OLAYI, h);
    }, [yukle]);

    const gorunen = randevular.filter(r => pasifGoster || (r.durum !== 'IPTAL' && r.durum !== 'ERTELENDI'));
    const bugunSayisi = randevular.filter(r => tarihStr(tarihOku(r.baslangic)) === tarihStr(new Date()) && (r.durum === 'PLANLANDI' || r.durum === 'ONAYLANDI')).length;

    return (
        <div className="rnd-kart">
            <div className="rnd-arac">
                <button className="tkp-btn kucuk" onClick={() => setTarih(new Date())}>Bugün</button>
                <div className="rnd-grup">
                    <button onClick={() => setTarih(kaydir(gorunum, tarih, -1))}><i className="fas fa-chevron-left"></i></button>
                    <button onClick={() => setTarih(kaydir(gorunum, tarih, 1))}><i className="fas fa-chevron-right"></i></button>
                </div>
                <span className="baslik">{baslikMetni(gorunum, tarih)}</span>
                {yukleniyor && <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8' }}></i>}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    {(tanimlar?.personel.length ?? 0) > 1 && (
                        <select value={personelID} onChange={e => setPersonelID(e.target.value === 'ekip' ? 'ekip' : Number(e.target.value))}>
                            {tanimlar!.personel.map(p => <option key={p.kullaniciID} value={p.kullaniciID}>{p.ben ? 'Benim takvimim' : p.adSoyad}</option>)}
                            <option value="ekip">Tüm ekip</option>
                        </select>
                    )}
                    <select value={turID} onChange={e => setTurID(e.target.value ? Number(e.target.value) : '')}>
                        <option value="">Tüm türler</option>
                        {(tanimlar?.turler ?? []).map(t => <option key={t.turID} value={t.turID}>{t.ad}</option>)}
                    </select>
                    <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={pasifGoster} onChange={e => setPasifGoster(e.target.checked)} />İptal / ertelenen
                    </label>
                    <div className="rnd-grup">
                        {([['gun', 'Gün'], ['hafta', 'Hafta'], ['ay', 'Ay']] as const).map(([g, ad]) => (
                            <button key={g} className={gorunum === g ? 'aktif' : ''} onClick={() => setGorunum(g)}>{ad}</button>
                        ))}
                    </div>
                </div>
            </div>
            {bugunSayisi > 0 && gorunum !== 'gun' && (
                <div style={{ padding: '6px 12px', fontSize: 12.5, color: '#c2410c', background: '#fff7ed', borderBottom: '1px solid #ffedd5' }}>
                    <i className="fas fa-bell" style={{ marginRight: 6 }}></i>Bugün {bugunSayisi} randevu var.
                </div>
            )}
            <RandevuTakvim
                gorunum={gorunum}
                tarih={tarih}
                randevular={gorunen}
                doluluk={doluluk}
                tekPersonelID={personelID === 'ekip' ? null : personelID}
                personelGoster={personelID === 'ekip'}
                onRandevuTikla={randevuAc}
                onBosTikla={(d) => onYeni({ baslangic: d, personelID: personelID === 'ekip' ? ben : personelID })}
                onGunTikla={(g) => { setTarih(g); setGorunum('gun'); }}
            />
        </div>
    );
};

// ── Ekip takvimi ───────────────────────────────────────────────────────

const EkipSekmesi: React.FC<{ onYeni: (v: RandevuVarsayilan) => void }> = ({ onYeni }) => {
    const [gorunum, setGorunum] = useState<'ekip' | 'ekipHafta'>('ekip');
    const [tarih, setTarih] = useState(new Date());
    const [randevular, setRandevular] = useState<RandevuOzet[]>([]);
    const [doluluk, setDoluluk] = useState<PersonelDoluluk[]>([]);
    const [yalnizAlanlar, setYalnizAlanlar] = useState(true);
    const [musaitlikKisi, setMusaitlikKisi] = useState<{ id: number; ad: string } | null>(null);
    const [yukleniyor, setYukleniyor] = useState(false);
    const [bas, son] = useMemo(() => aralik(gorunum, tarih), [gorunum, tarih]);

    const yukle = useCallback(async () => {
        setYukleniyor(true);
        try {
            const [r, d] = await Promise.all([
                axiosInstance.get('/api/Randevular', { params: { baslangic: yerelIso(bas), bitis: yerelIso(son), kapsam: 'ekip' } }),
                axiosInstance.get('/api/Randevular/doluluk', { params: { baslangic: yerelIso(bas), bitis: yerelIso(son) } }),
            ]);
            setRandevular(r.data);
            setDoluluk(d.data);
        } catch { /* sessiz */ }
        finally { setYukleniyor(false); }
    }, [bas, son]);
    useEffect(() => { yukle(); }, [yukle]);
    useEffect(() => {
        const h = () => yukle();
        window.addEventListener(RANDEVU_DEGISTI_OLAYI, h);
        return () => window.removeEventListener(RANDEVU_DEGISTI_OLAYI, h);
    }, [yukle]);

    const kolonlar = doluluk.filter(p => !yalnizAlanlar || p.randevuAlabilir || randevular.some(r => r.personelID === p.kullaniciID));
    const aktifler = randevular.filter(r => r.durum !== 'IPTAL' && r.durum !== 'ERTELENDI');

    // Haftalık doluluk: dolu saat / müsait saat
    const haftaGunleri = Array.from({ length: 7 }, (_, i) => gunEkle(haftaBasi(tarih), i));
    const musaitDk = (p: PersonelDoluluk, g: Date) => {
        const t = p.gunler.find(x => x.gun === g.getDay());
        if (!t) return p.gunler.length ? 0 : null;
        const m = (s?: string | null) => { if (!s) return 0; const [h, d] = s.split(':').map(Number); return h * 60 + d; };
        return m(t.bitisSaati) - m(t.baslangicSaati) - (t.molaBaslangic ? m(t.molaBitis) - m(t.molaBaslangic) : 0);
    };

    return (
        <div className="rnd-kart">
            <div className="rnd-arac">
                <button className="tkp-btn kucuk" onClick={() => setTarih(new Date())}>Bugün</button>
                <div className="rnd-grup">
                    <button onClick={() => setTarih(kaydir(gorunum, tarih, -1))}><i className="fas fa-chevron-left"></i></button>
                    <button onClick={() => setTarih(kaydir(gorunum, tarih, 1))}><i className="fas fa-chevron-right"></i></button>
                </div>
                <span className="baslik">{baslikMetni(gorunum, tarih)}</span>
                {yukleniyor && <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8' }}></i>}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <label style={{ fontSize: 12.5, display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                        <input type="checkbox" checked={yalnizAlanlar} onChange={e => setYalnizAlanlar(e.target.checked)} />Yalnızca randevu alanlar
                    </label>
                    <div className="rnd-grup">
                        <button className={gorunum === 'ekip' ? 'aktif' : ''} onClick={() => setGorunum('ekip')}>Gün</button>
                        <button className={gorunum === 'ekipHafta' ? 'aktif' : ''} onClick={() => setGorunum('ekipHafta')}>Hafta doluluğu</button>
                    </div>
                </div>
            </div>

            {gorunum === 'ekip' ? (
                <RandevuTakvim gorunum="ekip" tarih={tarih} randevular={aktifler} doluluk={kolonlar}
                    onRandevuTikla={randevuAc} onBosTikla={(d, pid) => onYeni({ baslangic: d, personelID: pid })} />
            ) : (
                <div style={{ overflowX: 'auto' }}>
                    <table className="rnd-tablo">
                        <thead>
                            <tr>
                                <th>Personel</th>
                                {haftaGunleri.map(g => <th key={tarihStr(g)} style={{ textAlign: 'center' }}>{GUN_KISA[g.getDay()]} {g.getDate()}</th>)}
                                <th style={{ textAlign: 'center' }}>Hafta</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {kolonlar.map(p => {
                                let topDolu = 0, topMusait = 0;
                                return (
                                    <tr key={p.kullaniciID} style={{ cursor: 'default' }}>
                                        <td style={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{p.adSoyad}{!p.randevuAlabilir && <div className="tkp-ipucu">randevu almıyor</div>}</td>
                                        {haftaGunleri.map(g => {
                                            const gunR = aktifler.filter(r => r.personelID === p.kullaniciID && tarihStr(tarihOku(r.baslangic)) === tarihStr(g));
                                            const dolu = gunR.reduce((t, r) => t + (tarihOku(r.bitis).getTime() - tarihOku(r.baslangic).getTime()) / 60000, 0);
                                            const izin = p.blokeler.some(b => b.tur === 'IZIN' && tarihOku(b.baslangic) <= g && tarihOku(b.bitis) > g);
                                            const mus = izin ? 0 : musaitDk(p, g);
                                            topDolu += dolu; if (mus) topMusait += mus;
                                            const oran = mus ? Math.min(100, Math.round(100 * dolu / mus)) : null;
                                            return (
                                                <td key={tarihStr(g)} style={{ textAlign: 'center', cursor: 'pointer', background: izin ? '#fef2f2' : mus === 0 ? '#f8fafc' : undefined }}
                                                    onClick={() => { setTarih(g); setGorunum('ekip'); }} title="Günü aç">
                                                    {izin ? <span style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>İzin</span>
                                                        : mus === 0 ? <span style={{ fontSize: 11, color: '#cbd5e1' }}>—</span>
                                                            : (
                                                                <>
                                                                    <div style={{ fontWeight: 700 }}>{gunR.length || ''}</div>
                                                                    {oran != null && <div className="rnd-cubuk"><span style={{ width: `${oran}%`, background: oran > 85 ? '#dc2626' : oran > 60 ? '#e67d21' : '#16a34a' }} /></div>}
                                                                </>
                                                            )}
                                                </td>
                                            );
                                        })}
                                        <td style={{ textAlign: 'center', fontWeight: 700 }}>
                                            {topMusait > 0 ? `%${Math.round(100 * topDolu / topMusait)}` : `${Math.round(topDolu / 60)} sa`}
                                        </td>
                                        <td><button className="tkp-btn kucuk" onClick={() => setMusaitlikKisi({ id: p.kullaniciID, ad: p.adSoyad })}><i className="fas fa-user-clock"></i>Müsaitlik</button></td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    <div className="tkp-ipucu" style={{ padding: '6px 12px' }}>Hücre: randevu sayısı ve çalışma saatine göre doluluk. Gün başlığına tıklayınca o günün ekip takvimi açılır.</div>
                </div>
            )}
            {gorunum === 'ekip' && kolonlar.length > 0 && (
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 12px', borderTop: '1px solid #eef1f6' }}>
                    <span className="tkp-ipucu" style={{ marginRight: 4 }}>Müsaitlik düzenle:</span>
                    {kolonlar.map(p => <button key={p.kullaniciID} className="tkp-btn kucuk" onClick={() => setMusaitlikKisi({ id: p.kullaniciID, ad: p.adSoyad })}>{p.adSoyad}</button>)}
                </div>
            )}
            {musaitlikKisi && (
                <div className="tkp-perde" onMouseDown={e => { if (e.target === e.currentTarget) setMusaitlikKisi(null); }}>
                    <div className="tkp-modal">
                        <div className="tkp-modal-bas">
                            <i className="fas fa-user-clock" style={{ color: '#e67d21' }}></i><h5>Müsaitlik — {musaitlikKisi.ad}</h5>
                            <button className="tkp-kapat" onClick={() => setMusaitlikKisi(null)}>×</button>
                        </div>
                        <div className="tkp-modal-govde"><MusaitlikAyarlari kullaniciID={musaitlikKisi.id} onKaydedildi={yukle} /></div>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Liste ──────────────────────────────────────────────────────────────

const ListeSekmesi: React.FC = () => {
    const { tanimlar } = useRandevuTanimlar();
    const bugun = new Date();
    const [bas, setBas] = useState(tarihStr(new Date(bugun.getFullYear(), bugun.getMonth(), 1)));
    const [son, setSon] = useState(tarihStr(new Date(bugun.getFullYear(), bugun.getMonth() + 1, 0)));
    const [kapsam, setKapsam] = useState<'ben' | 'ekip'>('ben');
    const [durum, setDurum] = useState('');
    const [turID, setTurID] = useState<number | ''>('');
    const [q, setQ] = useState('');
    const [liste, setListe] = useState<RandevuOzet[]>([]);
    const [yukleniyor, setYukleniyor] = useState(false);

    const yukle = useCallback(async () => {
        setYukleniyor(true);
        try {
            const r = await axiosInstance.get('/api/Randevular', {
                params: { baslangic: `${bas}T00:00:00`, bitis: `${tarihStr(gunEkle(new Date(son + 'T00:00:00'), 1))}T00:00:00`, kapsam, durum: durum || undefined, turID: turID || undefined, q: q.trim() || undefined },
            });
            setListe(r.data);
        } catch { setListe([]); }
        finally { setYukleniyor(false); }
    }, [bas, son, kapsam, durum, turID, q]);
    useEffect(() => { const z = window.setTimeout(yukle, q ? 300 : 0); return () => window.clearTimeout(z); }, [yukle, q]);
    useEffect(() => {
        const h = () => yukle();
        window.addEventListener(RANDEVU_DEGISTI_OLAYI, h);
        return () => window.removeEventListener(RANDEVU_DEGISTI_OLAYI, h);
    }, [yukle]);

    const csv = () => {
        const satirlar = [['Tarih', 'Saat', 'Başlık', 'Tür', 'Personel', 'Müşteri', 'Proje', 'Konum', 'Durum', 'Sonuç']];
        for (const r of liste) satirlar.push([
            tarihOku(r.baslangic).toLocaleDateString('tr-TR'), saatAraligi(r), r.baslik, r.turAdi ?? '', r.personelAdi ?? '', r.musteriAdi ?? '',
            r.projeAdi ?? '', r.konum ?? '', durumMeta(r.durum).ad, sonucAdi(r.sonuc) ?? '',
        ]);
        const metin = '﻿' + satirlar.map(s => s.map(h => `"${String(h).replace(/"/g, '""')}"`).join(';')).join('\r\n');
        const a = document.createElement('a');
        a.href = URL.createObjectURL(new Blob([metin], { type: 'text/csv;charset=utf-8' }));
        a.download = `randevular_${bas}_${son}.csv`;
        a.click();
    };

    return (
        <div className="rnd-kart">
            <div className="rnd-arac">
                <input type="date" value={bas} onChange={e => setBas(e.target.value)} />–<input type="date" value={son} onChange={e => setSon(e.target.value)} />
                {tanimlar?.yoneticiMi && (
                    <div className="rnd-grup">
                        <button className={kapsam === 'ben' ? 'aktif' : ''} onClick={() => setKapsam('ben')}>Benim</button>
                        <button className={kapsam === 'ekip' ? 'aktif' : ''} onClick={() => setKapsam('ekip')}>Ekip</button>
                    </div>
                )}
                <select value={durum} onChange={e => setDurum(e.target.value)}>
                    <option value="">Tüm durumlar</option>
                    <option value="PLANLANDI,ONAYLANDI">Bekleyen (planlı + onaylı)</option>
                    {DURUMLAR.map(d => <option key={d.d} value={d.d}>{d.ad}</option>)}
                </select>
                <select value={turID} onChange={e => setTurID(e.target.value ? Number(e.target.value) : '')}>
                    <option value="">Tüm türler</option>
                    {(tanimlar?.turler ?? []).map(t => <option key={t.turID} value={t.turID}>{t.ad}</option>)}
                </select>
                <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Ara…" style={{ minWidth: 160 }} />
                {yukleniyor && <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8' }}></i>}
                <button className="tkp-btn kucuk" style={{ marginLeft: 'auto' }} onClick={csv} disabled={!liste.length}><i className="fas fa-file-csv"></i>Excel (CSV)</button>
            </div>
            <div style={{ overflowX: 'auto' }}>
                <table className="rnd-tablo">
                    <thead><tr><th>Tarih</th><th>Saat</th><th>Randevu</th><th>Personel</th><th>Müşteri</th><th>Proje</th><th>Durum</th><th>Sonuç</th></tr></thead>
                    <tbody>
                        {liste.length === 0 && !yukleniyor && <tr><td colSpan={8} className="tkp-bos" style={{ textAlign: 'center', padding: 24 }}>Randevu yok.</td></tr>}
                        {liste.map(r => {
                            const dm = durumMeta(r.durum);
                            return (
                                <tr key={r.randevuID} onClick={() => randevuAc(r.randevuID)}>
                                    <td style={{ whiteSpace: 'nowrap' }}>{tarihOku(r.baslangic).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', weekday: 'short' })}</td>
                                    <td style={{ whiteSpace: 'nowrap' }}>{saatAraligi(r)}</td>
                                    <td><span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: r.renk, marginRight: 6 }}></span><b>{r.baslik}</b>{r.konum && <div className="tkp-ipucu">{r.konum}</div>}</td>
                                    <td>{r.personelAdi}</td>
                                    <td>{r.musteriAdi ?? '—'}{r.leadID && <span className="tkp-ipucu"> (potansiyel)</span>}</td>
                                    <td>{r.projeAdi ?? '—'}</td>
                                    <td><span className="rnd-durum" style={{ background: dm.zemin, color: dm.renk }}>{dm.ad}</span>{r.musteriOnay === 'ONAYLADI' && <i className="fas fa-user-check" style={{ color: '#0d9488', marginLeft: 6 }} title="Müşteri onayladı"></i>}</td>
                                    <td>{sonucAdi(r.sonuc) ?? '—'}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

// ── Raporlar ───────────────────────────────────────────────────────────

const RaporSekmesi: React.FC = () => {
    const bugun = new Date();
    const [bas, setBas] = useState(tarihStr(new Date(bugun.getFullYear(), bugun.getMonth(), 1)));
    const [son, setSon] = useState(tarihStr(new Date(bugun.getFullYear(), bugun.getMonth() + 1, 0)));
    const [veri, setVeri] = useState<any>(null);
    const [yukleniyor, setYukleniyor] = useState(false);

    useEffect(() => {
        setYukleniyor(true);
        axiosInstance.get('/api/Randevular/rapor', { params: { baslangic: `${bas}T00:00:00`, bitis: `${tarihStr(gunEkle(new Date(son + 'T00:00:00'), 1))}T00:00:00` } })
            .then(r => setVeri(r.data)).catch(() => setVeri(null)).finally(() => setYukleniyor(false));
    }, [bas, son]);

    const hazir = (ad: string, b: Date, s: Date) => (
        <button className="tkp-btn kucuk" onClick={() => { setBas(tarihStr(b)); setSon(tarihStr(s)); }}>{ad}</button>
    );
    const g = veri?.genel;
    const maxTur = Math.max(1, ...(veri?.tur ?? []).map((x: any) => x.sayi));
    const maxProje = Math.max(1, ...(veri?.proje ?? []).map((x: any) => x.sayi));
    const maxSonuc = Math.max(1, ...(veri?.sonuc ?? []).map((x: any) => x.sayi));

    return (
        <div>
            <div className="rnd-kart rnd-arac" style={{ marginBottom: 12 }}>
                <input type="date" value={bas} onChange={e => setBas(e.target.value)} />–<input type="date" value={son} onChange={e => setSon(e.target.value)} />
                {hazir('Bu ay', new Date(bugun.getFullYear(), bugun.getMonth(), 1), new Date(bugun.getFullYear(), bugun.getMonth() + 1, 0))}
                {hazir('Geçen ay', new Date(bugun.getFullYear(), bugun.getMonth() - 1, 1), new Date(bugun.getFullYear(), bugun.getMonth(), 0))}
                {hazir('Son 3 ay', new Date(bugun.getFullYear(), bugun.getMonth() - 2, 1), new Date(bugun.getFullYear(), bugun.getMonth() + 1, 0))}
                {hazir('Bu yıl', new Date(bugun.getFullYear(), 0, 1), new Date(bugun.getFullYear(), 11, 31))}
                {yukleniyor && <i className="fas fa-spinner fa-spin" style={{ color: '#94a3b8' }}></i>}
            </div>
            {g && (
                <>
                    <div className="rnd-ozet-izgara" style={{ marginBottom: 12 }}>
                        <div className="rnd-ozet"><div className="e">Toplam randevu</div><div className="d">{g.toplam}</div><div className="a">{veri.onlineSayisi} online · {g.bekleyen} bekleyen</div></div>
                        <div className="rnd-ozet"><div className="e">Tamamlanan</div><div className="d" style={{ color: '#16a34a' }}>{g.tamamlanan}</div><div className="a">Tamamlanma %{g.tamamlanmaOrani}</div></div>
                        <div className="rnd-ozet"><div className="e">Gelmedi</div><div className="d" style={{ color: '#7c3aed' }}>{g.gelmedi}</div><div className="a">Gelmeme %{g.gelmemeOrani}</div></div>
                        <div className="rnd-ozet"><div className="e">İptal / ertelenen</div><div className="d" style={{ color: '#dc2626' }}>{g.iptal} / {g.ertelenen}</div></div>
                        <div className="rnd-ozet"><div className="e">Satış</div><div className="d" style={{ color: '#e67d21' }}>{g.satis}</div><div className="a">Randevudan satışa %{g.satisDonusum}</div></div>
                        <div className="rnd-ozet"><div className="e">Teklif istendi</div><div className="d">{g.teklif}</div></div>
                    </div>

                    <div className="rnd-kart" style={{ marginBottom: 12, overflowX: 'auto' }}>
                        <div className="tkp-bolum-bas" style={{ padding: '12px 14px 6px', margin: 0 }}><i className="fas fa-user"></i>Personel bazlı</div>
                        <table className="rnd-tablo">
                            <thead><tr><th>Personel</th><th>Toplam</th><th>Tamamlanan</th><th>Gelmedi</th><th>İptal</th><th>Bekleyen</th><th>Tamamlanma</th><th>Gelmeme</th><th>Satış</th><th>Satışa dönüşüm</th></tr></thead>
                            <tbody>
                                {veri.personel.map((p: any) => (
                                    <tr key={p.kullaniciID} style={{ cursor: 'default' }}>
                                        <td style={{ fontWeight: 600 }}>{p.ad}</td><td>{p.ozet.toplam}</td><td>{p.ozet.tamamlanan}</td><td>{p.ozet.gelmedi}</td><td>{p.ozet.iptal}</td><td>{p.ozet.bekleyen}</td>
                                        <td>%{p.ozet.tamamlanmaOrani}</td><td style={{ color: p.ozet.gelmemeOrani > 20 ? '#dc2626' : undefined }}>%{p.ozet.gelmemeOrani}</td>
                                        <td>{p.ozet.satis}</td><td style={{ fontWeight: 700 }}>%{p.ozet.satisDonusum}</td>
                                    </tr>
                                ))}
                                {veri.personel.length === 0 && <tr><td colSpan={10} className="tkp-bos" style={{ textAlign: 'center' }}>Kayıt yok.</td></tr>}
                            </tbody>
                        </table>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
                        {[
                            { baslik: 'Randevu türüne göre', ikon: 'fa-tags', liste: veri.tur, max: maxTur, renk: (x: any) => x.renk },
                            { baslik: 'Projeye göre', ikon: 'fa-building', liste: veri.proje, max: maxProje, renk: () => '#1a2440' },
                            { baslik: 'Görüşme sonuçları', ikon: 'fa-clipboard-check', liste: veri.sonuc, max: maxSonuc, renk: (x: any) => x.sonuc === 'SATIS' ? '#e67d21' : '#0d9488' },
                        ].map(b => (
                            <div key={b.baslik} className="rnd-kart" style={{ padding: 14 }}>
                                <div className="tkp-bolum-bas"><i className={`fas ${b.ikon}`}></i>{b.baslik}</div>
                                {b.liste.length === 0 && <div className="tkp-bos">Kayıt yok.</div>}
                                {b.liste.map((x: any, i: number) => (
                                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 2fr auto', gap: 8, alignItems: 'center', fontSize: 12.5, marginBottom: 6 }}>
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={x.ad}>{x.ad}</span>
                                        <div className="rnd-cubuk"><span style={{ width: `${100 * x.sayi / b.max}%`, background: b.renk(x) }} /></div>
                                        <b>{x.sayi}{x.satis != null && x.satis > 0 && <span style={{ color: '#e67d21', fontWeight: 600 }}> · {x.satis} satış</span>}</b>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

// ── Ayarlar (türler, kaynaklar, genel) ─────────────────────────────────

const IKONLAR = ['fa-building', 'fa-hard-hat', 'fa-door-open', 'fa-search-location', 'fa-phone', 'fa-video', 'fa-handshake', 'fa-home', 'fa-key', 'fa-file-signature', 'fa-coffee', 'fa-car', 'fa-calendar'];

const AyarlarSekmesi: React.FC<{ onDegisti: () => void }> = ({ onDegisti }) => {
    const { tanimlar } = useRandevuTanimlar();
    const [genel, setGenel] = useState<RandevuGenelAyar | null>(null);
    const [turler, setTurler] = useState<RandevuTuru[]>([]);
    const [kaynaklar, setKaynaklar] = useState<RandevuKaynagi[]>([]);
    const [projeler, setProjeler] = useState<{ projeID: number; projeAdi: string }[]>([]);
    const [mesaj, setMesaj] = useState('');

    useEffect(() => {
        if (!tanimlar) return;
        setGenel(g => g ?? tanimlar.ayarlar);
        setTurler(tanimlar.turler);
        setKaynaklar(tanimlar.kaynaklar);
    }, [tanimlar]);
    useEffect(() => { randevuProjeleriGetir().then(setProjeler).catch(() => { }); }, []);

    const bildir = (m: string) => { setMesaj(m); window.setTimeout(() => setMesaj(''), 3000); };

    const genelKaydet = async () => {
        if (!genel) return;
        try { await axiosInstance.post('/api/Randevular/ayarlar', { ...genel, siteUrl: genel.siteUrl || window.location.origin }); bildir('Genel ayarlar kaydedildi.'); onDegisti(); }
        catch (e) { alert(hataMesaji(e)); }
    };
    const turKaydet = async (t: RandevuTuru) => {
        try { await axiosInstance.post('/api/Randevular/tur', t); bildir('Tür kaydedildi.'); onDegisti(); }
        catch (e) { alert(hataMesaji(e)); }
    };
    const kaynakKaydet = async (k: RandevuKaynagi) => {
        try { await axiosInstance.post('/api/Randevular/kaynak', k); bildir('Kaynak kaydedildi.'); onDegisti(); randevuDegisti(); }
        catch (e) { alert(hataMesaji(e)); }
    };
    const turDegistir = (i: number, alan: Partial<RandevuTuru>) => setTurler(prev => prev.map((t, j) => j === i ? { ...t, ...alan } : t));
    const kaynakDegistir = (i: number, alan: Partial<RandevuKaynagi>) => setKaynaklar(prev => prev.map((k, j) => j === i ? { ...k, ...alan } : k));
    const genelLink = `${window.location.origin}/randevu-al/genel`;

    if (!genel) return <div style={{ textAlign: 'center', padding: 30 }}><i className="fas fa-spinner fa-spin"></i></div>;
    const sayi = (v: string) => Math.max(0, parseInt(v || '0', 10) || 0);

    return (
        <div style={{ display: 'grid', gap: 14 }}>
            {mesaj && <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', borderRadius: 8, padding: '8px 12px', fontSize: 13 }}>{mesaj}</div>}

            <div className="rnd-kart" style={{ padding: 16 }}>
                <div className="tkp-bolum-bas"><i className="fas fa-sliders-h"></i>Genel</div>
                <div className="tkp-satir">
                    <div className="tkp-alan"><label className="tkp-etiket">Personel hatırlatması (dk önce)</label>
                        <input className="tkp-girdi" type="number" min={0} value={genel.personelHatirlatmaDk} onChange={e => setGenel({ ...genel, personelHatirlatmaDk: sayi(e.target.value) })} />
                        <div className="tkp-ipucu">Bildirim zili ile; 0 = kapalı. Randevuda ayrıca seçilebilir.</div></div>
                    <div className="tkp-alan"><label className="tkp-etiket">Müşteri hatırlatması (saat önce)</label>
                        <input className="tkp-girdi" type="number" min={0} value={genel.musteriHatirlatmaSaat} onChange={e => setGenel({ ...genel, musteriHatirlatmaSaat: sayi(e.target.value) })} />
                        <div className="tkp-ipucu">Yalnızca "müşteriye bildir" seçili randevularda; 0 = kapalı.</div></div>
                </div>
                <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 13, marginBottom: 10 }}>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={genel.musteriSms} onChange={e => setGenel({ ...genel, musteriSms: e.target.checked })} />Müşteriye SMS (Ayarlar › SMS Ayarları)</label>
                    <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}><input type="checkbox" checked={genel.musteriEposta} onChange={e => setGenel({ ...genel, musteriEposta: e.target.checked })} />Müşteriye e-posta (Gün Sonu Raporu SMTP ayarları)</label>
                </div>
                <div className="tkp-bolum-bas" style={{ marginTop: 6 }}><i className="fas fa-globe"></i>Online randevu</div>
                <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginBottom: 8 }}>
                    <input type="checkbox" checked={genel.onlineRandevuAktif} onChange={e => setGenel({ ...genel, onlineRandevuAktif: e.target.checked })} />Online randevu açık (personel kendi müsaitliğinden ayrıca açar)
                </label>
                <div className="tkp-satir">
                    <div className="tkp-alan"><label className="tkp-etiket">En erken (saat sonrası)</label><input className="tkp-girdi" type="number" min={0} value={genel.onlineMinSaat} onChange={e => setGenel({ ...genel, onlineMinSaat: sayi(e.target.value) })} /></div>
                    <div className="tkp-alan"><label className="tkp-etiket">En geç (gün ileri)</label><input className="tkp-girdi" type="number" min={1} value={genel.onlineMaxGun} onChange={e => setGenel({ ...genel, onlineMaxGun: Math.max(1, sayi(e.target.value)) })} /></div>
                    <div className="tkp-alan"><label className="tkp-etiket">Site adresi (SMS bağlantıları)</label><input className="tkp-girdi" value={genel.siteUrl ?? ''} placeholder={window.location.origin} onChange={e => setGenel({ ...genel, siteUrl: e.target.value })} /></div>
                </div>
                {genel.onlineRandevuAktif && (
                    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '8px 10px', fontSize: 12.5, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                        Firma geneli bağlantı (online randevuya açık tüm personel): <code>{genelLink}</code>
                        <button className="tkp-btn kucuk" onClick={() => navigator.clipboard?.writeText(genelLink).then(() => bildir('Bağlantı kopyalandı.'))}><i className="fas fa-copy"></i>Kopyala</button>
                    </div>
                )}
                <div style={{ textAlign: 'right' }}><button className="tkp-btn ana" onClick={genelKaydet}><i className="fas fa-check"></i>Kaydet</button></div>
            </div>

            <div className="rnd-kart" style={{ padding: 16, overflowX: 'auto' }}>
                <div className="tkp-bolum-bas"><i className="fas fa-tags"></i>Randevu türleri
                    <span className="sag"><button className="tkp-btn kucuk" onClick={() => setTurler(p => [...p, { turID: 0, ad: '', renk: '#2563eb', ikon: 'fa-calendar', varsayilanSure: 60, siraNo: p.length + 1, aktifMi: true }])}><i className="fas fa-plus"></i>Tür ekle</button></span>
                </div>
                <table className="rnd-tablo">
                    <thead><tr><th>Ad</th><th>Renk</th><th>İkon</th><th>Varsayılan süre (dk)</th><th>Sıra</th><th>Aktif</th><th></th></tr></thead>
                    <tbody>
                        {turler.map((t, i) => (
                            <tr key={t.turID || `y${i}`} style={{ cursor: 'default' }}>
                                <td><input className="tkp-girdi" value={t.ad} onChange={e => turDegistir(i, { ad: e.target.value })} /></td>
                                <td><input type="color" value={t.renk} onChange={e => turDegistir(i, { renk: e.target.value })} style={{ width: 44, height: 32, border: 'none', background: 'none' }} /></td>
                                <td>
                                    <select className="tkp-girdi" value={t.ikon ?? ''} onChange={e => turDegistir(i, { ikon: e.target.value })} style={{ width: 150 }}>
                                        {IKONLAR.map(x => <option key={x} value={x}>{x.replace('fa-', '')}</option>)}
                                    </select>
                                    <i className={`fas ${t.ikon}`} style={{ marginLeft: 6, color: t.renk }}></i>
                                </td>
                                <td><input className="tkp-girdi" type="number" min={5} step={5} value={t.varsayilanSure} onChange={e => turDegistir(i, { varsayilanSure: sayi(e.target.value) })} style={{ width: 90 }} /></td>
                                <td><input className="tkp-girdi" type="number" value={t.siraNo} onChange={e => turDegistir(i, { siraNo: sayi(e.target.value) })} style={{ width: 70 }} /></td>
                                <td><input type="checkbox" checked={t.aktifMi} onChange={e => turDegistir(i, { aktifMi: e.target.checked })} /></td>
                                <td><button className="tkp-btn kucuk ana" onClick={() => turKaydet(t)}><i className="fas fa-check"></i></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="rnd-kart" style={{ padding: 16, overflowX: 'auto' }}>
                <div className="tkp-bolum-bas"><i className="fas fa-cubes"></i>Kaynaklar (toplantı odası, numune daire, araç)
                    <span className="sag"><button className="tkp-btn kucuk" onClick={() => setKaynaklar(p => [...p, { kaynakID: 0, ad: '', tur: 'ODA', aktifMi: true }])}><i className="fas fa-plus"></i>Kaynak ekle</button></span>
                </div>
                <div className="tkp-ipucu" style={{ marginBottom: 6 }}>Bir kaynak aynı saatte iki randevuya verilemez.</div>
                <table className="rnd-tablo">
                    <thead><tr><th>Ad</th><th>Tür</th><th>Proje</th><th>Açıklama</th><th>Aktif</th><th></th></tr></thead>
                    <tbody>
                        {kaynaklar.length === 0 && <tr><td colSpan={6} className="tkp-bos">Kaynak tanımlı değil.</td></tr>}
                        {kaynaklar.map((k, i) => (
                            <tr key={k.kaynakID || `y${i}`} style={{ cursor: 'default' }}>
                                <td><input className="tkp-girdi" value={k.ad} onChange={e => kaynakDegistir(i, { ad: e.target.value })} /></td>
                                <td><select className="tkp-girdi" value={k.tur} onChange={e => kaynakDegistir(i, { tur: e.target.value })}>{KAYNAK_TURLERI.map(x => <option key={x.t} value={x.t}>{x.ad}</option>)}</select></td>
                                <td><select className="tkp-girdi" value={k.projeID ?? ''} onChange={e => kaynakDegistir(i, { projeID: e.target.value ? Number(e.target.value) : null })}>
                                    <option value="">Tüm projeler</option>{projeler.map(p => <option key={p.projeID} value={p.projeID}>{p.projeAdi}</option>)}</select></td>
                                <td><input className="tkp-girdi" value={k.aciklama ?? ''} onChange={e => kaynakDegistir(i, { aciklama: e.target.value })} /></td>
                                <td><input type="checkbox" checked={k.aktifMi} onChange={e => kaynakDegistir(i, { aktifMi: e.target.checked })} /></td>
                                <td><button className="tkp-btn kucuk ana" onClick={() => kaynakKaydet(k)}><i className="fas fa-check"></i></button></td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default Randevular;
